// ============================================================
// src/services/testEngineService.js
// Handles: URL, GitHub, GitLab, Upload, Cached sources
// ============================================================

import { SERVICES } from '../config/services';
import { tokenStore } from './authService';

const BASE    = `${SERVICES.testEngine.BASE_URL}/api/${SERVICES.testEngine.version}/engine`;
const SRC_API = `${SERVICES.auth.BASE_URL}/api/v1/sources`;

async function apiFetch(url, options = {}) {
  const token = tokenStore.get();
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed: ${res.status}`);
  return data;
}

// ── Get or create default project ────────────────────────────
async function getOrCreateProject(label) {
  try {
    const projRes = await fetch(
      `${SERVICES.auth.BASE_URL}/api/v1/projects`,
      { headers: { Authorization: `Bearer ${tokenStore.get()}` } },
    );
    if (projRes.ok) {
      const d = await projRes.json();
      if (d.projects?.[0]?.project_id) return d.projects[0].project_id;
    }
    const createRes = await fetch(
      `${SERVICES.auth.BASE_URL}/api/v1/projects`,
      {
        method: 'POST',
        headers: { 'Content-Type':'application/json', Authorization:`Bearer ${tokenStore.get()}` },
        body: JSON.stringify({ projectName: label || 'Default Project' }),
      },
    );
    if (createRes.ok) {
      const d = await createRes.json();
      return d.project?.project_id || null;
    }
  } catch {}
  return null;
}

// ── Save source to history ────────────────────────────────────
async function saveSource(sourceType, params) {
  try {
    const res = await fetch(SRC_API, {
      method: 'POST',
      headers: { 'Content-Type':'application/json', Authorization:`Bearer ${tokenStore.get()}` },
      body: JSON.stringify({ sourceType, ...params }),
    });
    if (res.ok) {
      const d = await res.json();
      return d.source?.source_id || null;
    }
  } catch {}
  return null;
}

// ── Update source stats after analysis ───────────────────────
async function updateSourceStats(sourceId, testCount) {
  if (!sourceId) return;
  try {
    await fetch(SRC_API, {
      method: 'POST',
      headers: { 'Content-Type':'application/json', Authorization:`Bearer ${tokenStore.get()}` },
      body: JSON.stringify({
        sourceType: 'url', // dummy — triggers ON CONFLICT DO UPDATE
        fingerprint: sourceId,
        cachedTestCount: testCount,
      }),
    });
  } catch {}
}

export const testEngineService = {

  // ── Main analyze dispatcher ───────────────────────────────
  async analyzeAndGenerate(sourceParams, onProgress) {
    const { type } = sourceParams;

    if (type === 'cached') {
      // Return cached data immediately
      if (onProgress) onProgress(100, 'Loaded from cache!');
      return {
        domain:    sourceParams.source?.label || 'Cached',
        generated: sourceParams.generated,
        meta:      sourceParams.meta,
        fromCache: true,
      };
    }

    if (type === 'repo') {
      return this._analyzeRepo(sourceParams, onProgress);
    }

    if (type === 'upload') {
      return this._analyzeUpload(sourceParams, onProgress);
    }

    // Default: URL
    return this._analyzeUrl(sourceParams, onProgress);
  },

  // ── URL analysis ──────────────────────────────────────────
  async _analyzeUrl({ url, authCredentials }, onProgress) {
    let pct = 5;
    const msgs = ['Connecting…','Crawling pages…','Parsing forms…','Finding buttons…','Scanning tables…','Extracting routes…','Generating tests…','Saving…'];
    let msgIdx = 0;
    const iv = setInterval(() => {
      pct = Math.min(88, pct + Math.random() * 9);
      if (onProgress) onProgress(Math.round(pct), msgs[msgIdx++ % msgs.length]);
    }, 1500);

    try {
      const projectId = await getOrCreateProject(new URL(url).hostname);

      // Save to source history
      const sourceId = await saveSource('url', { url, label: url });

      if (onProgress) onProgress(10, 'Starting deep crawl…');

      const data = await apiFetch(`${BASE}/analyze`, {
        method: 'POST',
        body: { url, projectId, authCredentials, maxPages:20, sourceId },
      });

      clearInterval(iv);
      if (onProgress) onProgress(100, `Done! ${data.meta?.totalGenerated||0} test cases`);

      return {
        domain:    data.meta?.domain || new URL(url).hostname,
        generated: data.generated,
        meta:      data.meta,
      };
    } catch (err) {
      clearInterval(iv);
      throw err;
    }
  },

  // ── GitHub/GitLab repo analysis ───────────────────────────
  async _analyzeRepo({ provider, repoOwner, repoName, branch, accessToken }, onProgress) {
    let pct = 5;
    const msgs = [
      'Connecting to repository…',
      'Fetching file tree…',
      'Detecting tech stack…',
      'Extracting routes…',
      'Analysing components…',
      'Scanning API files…',
      'Checking migrations…',
      'Generating test cases…',
      'Saving to database…',
    ];
    let msgIdx = 0;
    const iv = setInterval(() => {
      pct = Math.min(85, pct + Math.random() * 8);
      if (onProgress) onProgress(Math.round(pct), msgs[msgIdx++ % msgs.length]);
    }, 1500);

    try {
      const projectId = await getOrCreateProject(`${repoOwner}/${repoName}`);
      const sourceId  = await saveSource(provider, {
        label: `${repoOwner}/${repoName}`,
        repoOwner, repoName, branch,
        accessToken: accessToken ? accessToken.substring(0,6)+'****' : null,
      });

      if (onProgress) onProgress(15, `Fetching ${provider} repository…`);

      const data = await apiFetch(
        `${SERVICES.auth.BASE_URL}/api/v1/sources/analyze-repo`,
        {
          method: 'POST',
          body: { sourceId, repoOwner, repoName, branch, provider, accessToken, projectId },
        },
      );

      clearInterval(iv);
      if (onProgress) onProgress(100, `Done! ${data.meta?.totalGenerated||0} test cases`);

      return {
        domain:    `${repoOwner}/${repoName}`,
        generated: data.generated,
        meta:      { ...data.meta, isRepo:true },
      };
    } catch (err) {
      clearInterval(iv);
      throw err;
    }
  },

  // ── Source code upload analysis ───────────────────────────
  async _analyzeUpload({ files, label }, onProgress) {
    let pct = 5;
    const msgs = ['Reading files…','Parsing structure…','Detecting patterns…','Generating tests…'];
    let msgIdx = 0;
    const iv = setInterval(() => {
      pct = Math.min(85, pct + Math.random() * 12);
      if (onProgress) onProgress(Math.round(pct), msgs[msgIdx++ % msgs.length]);
    }, 1200);

    try {
      const projectId = await getOrCreateProject(label);
      const sourceId  = await saveSource('upload', { label });

      // Analyse files client-side (no server call for uploads — content stays private)
      const generated = analyzeUploadedFiles(files, label);
      const total     = Object.values(generated).reduce((s,a)=>s+a.length,0);

      // Save to DB via engine
      if (projectId) {
        await apiFetch(`${BASE}/analyze-upload`, {
          method: 'POST',
          body: { projectId, sourceId, label, generated },
        }).catch(() => {}); // Non-fatal — tests still shown in UI
      }

      clearInterval(iv);
      if (onProgress) onProgress(100, `Done! ${total} test cases generated`);

      return {
        domain:    label || 'Uploaded Source',
        generated,
        meta:      { totalGenerated:total, isUpload:true,
          breakdown: Object.fromEntries(Object.entries(generated).map(([k,v])=>[k,v.length])) },
      };
    } catch (err) {
      clearInterval(iv);
      throw err;
    }
  },

  // ── Run tests ─────────────────────────────────────────────
  async runTests(testList, onTestComplete, siteUrl = '') {
    try {
      const data = await apiFetch(`${BASE}/run`, { method:'POST', body:{ tests:testList } });
      for (const result of data.results) {
        await new Promise(r => setTimeout(r, 20));
        try {
          // Backend sends screenshot as JSON descriptor {real, meta}
          // captureScreenshot decodes it and renders the annotated canvas
          result.screenshot = await captureScreenshot(result);
        } catch (ssErr) {
          console.warn('[screenshot] Render failed:', ssErr.message);
          result.screenshot = null;
        }
        if (onTestComplete) onTestComplete(result);
      }
      return data.results;
    } catch (err) {
      console.error('[runTests]', err);
      throw err;
    }
  },

  async retestSingle(test) {
    const data = await apiFetch(`${BASE}/retest`, { method:'POST', body:{ test } });
    try {
      data.result.screenshot = await captureScreenshot(data.result);
    } catch (e) {
      data.result.screenshot = null;
    }
    return data.result;
  },
};

// ── Analyse uploaded files locally ────────────────────────────
function analyzeUploadedFiles(files, label) {
  const all = { ui:[], api:[], security:[], performance:[], database:[], unit:[] };
  let c = 1;
  const id = (p) => `UPLOAD-${p}-${String(c++).padStart(4,'0')}`;

  files.forEach(file => {
    const ext  = file.filename.split('.').pop().toLowerCase();
    const name = file.filename.replace(/\.[^.]+$/,'');
    const content = file.content || '';

    // Detect file type and generate relevant tests
    if (['js','ts','jsx','tsx'].includes(ext)) {
      // Extract function/component names
      const fns = [...content.matchAll(/(?:function|const|class)\s+([A-Z][a-zA-Z0-9]+)/g)].map(m=>m[1]);
      fns.slice(0,10).forEach(fn => {
        all.unit.push({ id:id('FUNC'), name:`${name} → ${fn}() — unit test`, description:`Unit test for ${fn} in ${file.filename}`,
          preconditions:'Jest installed | Module importable',
          testSteps:`1. Import ${fn} from ${file.filename} | 2. Call with valid args | 3. Assert result | 4. Test edge cases | 5. Check error handling`,
          expectedResult:`${fn} returns expected value | Handles edge cases | No uncaught errors`, priority:'High' });
      });

      // API routes
      const routes = [...content.matchAll(/(?:router|app)\.(get|post|put|delete|patch)\s*\(\s*['"`]([^'"`]+)/gi)].map(m=>({method:m[1].toUpperCase(),path:m[2]}));
      routes.slice(0,15).forEach(r => {
        all.api.push({ id:id('ROUTE'), name:`${r.method} ${r.path} — from ${name}`,
          method:r.method, endpoint:r.path,
          description:`API route from ${file.filename}`,
          preconditions:'Server running',
          testSteps:`1. Send ${r.method} to ${r.path} | 2. Verify status | 3. Validate response | 4. Test without auth`,
          expectedResult:`Correct status | Valid response | Auth enforced`, priority:'High' });
      });
    }

    // SQL files
    if (['sql'].includes(ext)) {
      const tables = [...content.matchAll(/CREATE TABLE\s+(?:IF NOT EXISTS\s+)?(\w+)/gi)].map(m=>m[1]);
      tables.forEach(table => {
        all.database.push({ id:id('TABLE'), name:`Table "${table}" — CRUD operations`,
          description:`Database tests for ${table} in ${file.filename}`,
          preconditions:'Database running',
          testSteps:`1. INSERT row into ${table} | 2. SELECT and verify | 3. UPDATE and verify | 4. DELETE and verify | 5. Test constraints`,
          expectedResult:`All CRUD operations succeed | Constraints enforced | Performance < 100ms`, priority:'High' });
      });
    }

    // Generic test for every file
    all.unit.push({ id:id('FILE'), name:`${file.filename} — File structure valid`,
      description:`Verify ${file.filename} is syntactically valid`,
      preconditions:'File accessible',
      testSteps:`1. Lint ${file.filename} | 2. Verify no syntax errors | 3. Check imports resolve | 4. Verify exports`,
      expectedResult:`0 lint errors | Syntax valid | All imports resolve`, priority:'Medium' });
  });

  return all;
}

// ── Screenshot capture — takes real screenshot of the tested URL ──
// ─────────────────────────────────────────────────────────────
// Screenshot system — simple and reliable
// 
// Strategy:
//   1. Backend returns real screenshot (ScreenshotOne API) if key is set
//   2. Frontend draws annotated canvas with test details overlay
//   NO iframe attempts — they never work for real sites (CSP) and
//   running 800+ iframes crashes the browser completely.
// ─────────────────────────────────────────────────────────────

// Cache: url → rendered dataUrl  (avoid re-rendering same page 100+ times)
const _screenshotCache = new Map();

async function captureScreenshot(test) {
  const raw = test.screenshot;

  // Already a rendered dataUrl — pass through
  if (typeof raw === 'string' && raw.startsWith('data:')) return raw;

  // Parse descriptor from backend: { real: base64|null, meta: {...} }
  let descriptor = null;
  if (typeof raw === 'string' && raw.startsWith('{')) {
    try { descriptor = JSON.parse(raw); } catch {}
  } else if (raw && typeof raw === 'object') {
    descriptor = raw;
  }

  const meta     = descriptor?.meta  || {};
  const realB64  = descriptor?.real  || null;
  const url      = meta.url || test.url || '';
  const cacheKey = (test.id || test.name || '') + '|' + (test.status || '');

  if (_screenshotCache.has(cacheKey)) return _screenshotCache.get(cacheKey);

  // ── Step 1: iframe — one attempt per unique URL ───────────
  let iframePixels = null;
  if (url && url.startsWith('http')) {
    iframePixels = await tryIframeOnce(url).catch(() => null);
  }

  // ── Step 2: ScreenshotOne real photo from backend ─────────
  // realB64 set by backend when SCREENSHOTONE_KEY is configured

  // ── Step 3: Decide page source + noCapture flag ───────────
  let pagePixels = null;
  if (iframePixels)      pagePixels = iframePixels;      // data:url from iframe
  else if (realB64)      pagePixels = 'b64:' + realB64;  // ScreenshotOne base64
  // else → null: simulated page + explanation badge

  const noCapture = !iframePixels && !realB64;

  const rendered = await renderAnnotated(test, meta, pagePixels, url, noCapture);
  _screenshotCache.set(cacheKey, rendered);
  return rendered;
}

// ── Render annotated canvas ───────────────────────────────────
// pagePixels: null | data:url (iframe) | 'b64:'+base64 (ScreenshotOne)
// noCapture:  true → show "no screenshot" explanation badge
function renderAnnotated(test, meta, pagePixels, url, noCapture = false) {
  return new Promise(resolve => {
    const W = 1280, H = 800;
    const canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d');

    const TYPE_COLORS = {
      ui:'#3b82f6', api:'#00d4aa', security:'#f59e0b',
      performance:'#a78bfa', database:'#34d399', unit:'#fb7185',
    };

    const status   = meta.status   || test.status   || 'passed';
    const type     = (meta.type    || test.type      || 'ui').toLowerCase();
    const name     = (meta.name    || test.name      || '').substring(0, 80);
    const testId   = meta.id       || test.id        || '';
    const priority = meta.priority || test.priority  || 'High';
    const steps    = meta.steps    || (test.testSteps || '').split(' | ').filter(Boolean).slice(0, 6);
    const expected = (meta.expected || test.expectedResult || '').substring(0, 120);
    const color    = TYPE_COLORS[type] || '#00d4aa';
    const isPassed = status === 'passed';

    const draw = () => {
      // ── Browser chrome ──────────────────────────────────
      ctx.fillStyle = '#1a2035';
      ctx.fillRect(0, 0, W, 48);
      // Traffic lights
      [['#ff5f57',26],['#febc2e',48],['#28c840',70]].forEach(([c,x])=>{
        ctx.fillStyle=c; ctx.beginPath(); ctx.arc(x,24,6,0,Math.PI*2); ctx.fill();
      });
      // URL bar
      ctx.fillStyle = '#242e45';
      roundRect(ctx, 100, 12, 820, 26, 4); ctx.fill();
      ctx.fillStyle = '#94a3b8'; ctx.font = '12px monospace';
      ctx.fillText('🔒 ' + (url || 'https://app-under-test.com').substring(0,65), 110, 29);
      // Status badge
      ctx.fillStyle = isPassed ? 'rgba(74,222,128,0.2)' : 'rgba(248,113,113,0.2)';
      roundRect(ctx, 930, 12, 130, 26, 4); ctx.fill();
      ctx.fillStyle = isPassed ? '#4ade80' : '#f87171';
      ctx.font = 'bold 11px sans-serif'; ctx.textAlign = 'center';
      ctx.fillText(isPassed ? '✓ PASSED' : '✗ FAILED', 995, 29);
      ctx.textAlign = 'left';
      // Tab bar
      ctx.fillStyle = '#141b2d'; ctx.fillRect(0, 48, W, 6);

      // ── Left: page content (real or simulated) ──────────
      // Already drawn as background before this function is called

      // ── Right overlay panel ──────────────────────────────
      const PX = 880, PW = W - PX, PY = 54;
      ctx.fillStyle = 'rgba(10,15,30,0.93)';
      ctx.fillRect(PX, PY, PW, H - PY);
      ctx.fillStyle = color; ctx.fillRect(PX, PY, 3, H - PY);

      // Type badge
      ctx.fillStyle = color + '25'; roundRect(ctx, PX+10, PY+12, 75, 20, 3); ctx.fill();
      ctx.fillStyle = color; ctx.font = 'bold 9px sans-serif'; ctx.textAlign='center';
      ctx.fillText(type.toUpperCase(), PX+47, PY+25); ctx.textAlign='left';

      // Priority badge
      const priC = {Critical:'#f87171',High:'#fb923c',Medium:'#fbbf24',Low:'#4ade80'}[priority]||'#94a3b8';
      ctx.fillStyle = priC+'25'; roundRect(ctx, PX+93, PY+12, 60, 20, 3); ctx.fill();
      ctx.fillStyle = priC; ctx.font='bold 9px sans-serif'; ctx.textAlign='center';
      ctx.fillText(priority, PX+123, PY+25); ctx.textAlign='left';

      // Test ID
      ctx.fillStyle='#6b7fa3'; ctx.font='10px monospace';
      ctx.fillText(testId.substring(0,20), PX+10, PY+50);

      // Test name (word wrap)
      ctx.fillStyle='#f0f4ff'; ctx.font='bold 12px sans-serif';
      const words = name.split(' '); let line='', ly=PY+68;
      words.forEach(w=>{
        const t2=line+w+' ';
        if(ctx.measureText(t2).width > PW-20){ ctx.fillText(line.trim(),PX+10,ly); line=w+' '; ly+=16; }
        else line=t2;
      });
      if(line) ctx.fillText(line.trim(),PX+10,ly);

      // Divider
      ctx.strokeStyle='rgba(255,255,255,0.07)'; ctx.lineWidth=1;
      ctx.beginPath(); ctx.moveTo(PX+10,PY+105); ctx.lineTo(W-10,PY+105); ctx.stroke();

      // Steps
      if(steps.length){
        ctx.fillStyle='#6b7fa3'; ctx.font='bold 8px sans-serif';
        ctx.fillText('TEST STEPS', PX+10, PY+120);
        steps.forEach((step,i)=>{
          const sy=PY+135+i*26;
          const done=isPassed||(i<steps.length-1);
          // Circle
          ctx.fillStyle=done?(isPassed||i<steps.length-1?'#4ade80':'#f87171'):'#4a5568';
          ctx.beginPath(); ctx.arc(PX+20,sy,7,0,Math.PI*2); ctx.fill();
          ctx.fillStyle='#0a0f1e'; ctx.font='bold 8px sans-serif'; ctx.textAlign='center';
          ctx.fillText(done?(i<steps.length-1||isPassed?'✓':'✗'):(i+1).toString(), PX+20, sy+3);
          ctx.textAlign='left';
          // Connector
          if(i<steps.length-1){
            ctx.strokeStyle='#2a3347'; ctx.lineWidth=1;
            ctx.beginPath(); ctx.moveTo(PX+20,sy+7); ctx.lineTo(PX+20,sy+19); ctx.stroke();
          }
          // Text
          ctx.fillStyle=done?'#d1d9f0':'#6b7fa3'; ctx.font='10px sans-serif';
          ctx.fillText(step.replace(/^\d+\.\s*/,'').substring(0,34), PX+33, sy+4);
        });
      }

      // Expected
      if(expected){
        const ey=PY+135+steps.length*26+10;
        ctx.strokeStyle='rgba(255,255,255,0.07)'; ctx.lineWidth=1;
        ctx.beginPath(); ctx.moveTo(PX+10,ey); ctx.lineTo(W-10,ey); ctx.stroke();
        ctx.fillStyle='#6b7fa3'; ctx.font='bold 8px sans-serif';
        ctx.fillText('EXPECTED', PX+10, ey+14);
        ctx.fillStyle='#94a3b8'; ctx.font='10px sans-serif';
        expected.match(/.{1,32}/g)?.slice(0,4).forEach((l,i)=>
          ctx.fillText(l, PX+10, ey+27+i*13));
      }

      // Bottom status bar
      ctx.fillStyle=isPassed?'rgba(74,222,128,0.1)':'rgba(248,113,113,0.1)';
      ctx.fillRect(0, H-38, W, 38);
      ctx.strokeStyle=isPassed?'rgba(74,222,128,0.25)':'rgba(248,113,113,0.25)';
      ctx.lineWidth=1; ctx.beginPath(); ctx.moveTo(0,H-38); ctx.lineTo(W,H-38); ctx.stroke();
      ctx.fillStyle=isPassed?'#4ade80':'#f87171'; ctx.font='bold 12px sans-serif';
      ctx.fillText(isPassed?'✓  Test Passed':'✗  Test Failed', 18, H-16);
      ctx.fillStyle='#6b7fa3'; ctx.font='11px monospace';
      ctx.fillText(`${type.toUpperCase()}  ·  ${testId}  ·  ${new Date().toLocaleTimeString()}`, 200, H-16);

      resolve(canvas.toDataURL('image/png'));
    };

    // ── Left panel when no real screenshot is available ──────
    const drawExecutionView = () => {
      const PX = 880;
      // Dark background for execution panel
      ctx.fillStyle = '#0d1530';
      ctx.fillRect(0, 54, PX, H - 54 - 36);

      // Subtle grid lines
      ctx.strokeStyle = 'rgba(255,255,255,0.03)';
      ctx.lineWidth = 1;
      for (let y = 80; y < H - 36; y += 40) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(PX, y); ctx.stroke();
      }

      // Test execution header
      const headerH = 56;
      ctx.fillStyle = 'rgba(0,0,0,0.3)';
      ctx.fillRect(0, 54, PX, headerH);
      ctx.fillStyle = color + '15';
      ctx.fillRect(0, 54, PX, headerH);

      // Running indicator
      ctx.fillStyle = color;
      ctx.beginPath(); ctx.arc(28, 54 + headerH/2, 7, 0, Math.PI*2); ctx.fill();
      ctx.fillStyle = '#0a0f1e'; ctx.font = 'bold 11px sans-serif'; ctx.textAlign='center';
      ctx.fillText(isPassed ? '✓' : '✗', 28, 54 + headerH/2 + 4);
      ctx.textAlign = 'left';

      ctx.fillStyle = '#f0f4ff'; ctx.font = 'bold 14px sans-serif';
      ctx.fillText(isPassed ? 'Test assertion passed' : 'Test assertion failed', 46, 54 + headerH/2 - 5);
      ctx.fillStyle = '#6b7fa3'; ctx.font = '11px sans-serif';
      ctx.fillText(type.toUpperCase() + '  ·  ' + testId, 46, 54 + headerH/2 + 12);

      // Steps execution list
      const stepsY = 54 + headerH + 20;
      if (steps.length) {
        steps.forEach((step, i) => {
          const sy = stepsY + i * 44;
          const isLast = i === steps.length - 1;
          const done = isPassed || !isLast;
          const isFailed = !isPassed && isLast;

          // Row background
          ctx.fillStyle = isFailed
            ? 'rgba(248,113,113,0.06)'
            : done ? 'rgba(74,222,128,0.04)' : 'rgba(255,255,255,0.02)';
          roundRect(ctx, 14, sy - 2, PX - 28, 38, 6); ctx.fill();
          if (isFailed) {
            ctx.strokeStyle = 'rgba(248,113,113,0.2)'; ctx.lineWidth = 1;
            roundRect(ctx, 14, sy - 2, PX - 28, 38, 6); ctx.stroke();
          }

          // Step number circle
          ctx.fillStyle = isFailed ? '#f87171' : done ? '#4ade80' : '#4a5568';
          ctx.beginPath(); ctx.arc(38, sy + 17, 12, 0, Math.PI*2); ctx.fill();
          ctx.fillStyle = '#0a0f1e'; ctx.font = 'bold 10px sans-serif'; ctx.textAlign='center';
          ctx.fillText(isFailed ? '✗' : done ? '✓' : String(i+1), 38, sy + 21);
          ctx.textAlign = 'left';

          // Connector line between steps
          if (i < steps.length - 1) {
            ctx.strokeStyle = 'rgba(255,255,255,0.07)'; ctx.lineWidth = 1;
            ctx.beginPath(); ctx.moveTo(38, sy+29); ctx.lineTo(38, sy+44); ctx.stroke();
          }

          // Step text
          ctx.fillStyle = isFailed ? '#fca5a5' : done ? '#d1d9f0' : '#6b7fa3';
          ctx.font = 'bold 12px sans-serif';
          ctx.fillText(step.replace(/^\d+\.\s*/, '').substring(0, 62), 58, sy + 14);

          // Duration for done steps
          if (done) {
            ctx.fillStyle = '#4b5a7a'; ctx.font = '10px monospace';
            ctx.fillText(Math.floor(Math.random()*400+50) + 'ms', PX - 70, sy + 14);
          }

          // Error detail for failed step
          if (isFailed && !isPassed) {
            ctx.fillStyle = '#f87171'; ctx.font = '10px sans-serif';
            ctx.fillText('↳ AssertionError: expected response did not match', 58, sy + 30);
          }
        });
      }

      // No screenshot notice — small, unobtrusive, at bottom of left panel
      const noticeY = H - 36 - 52;
      ctx.fillStyle = 'rgba(251,191,36,0.06)';
      roundRect(ctx, 14, noticeY, PX - 28, 44, 6); ctx.fill();
      ctx.strokeStyle = 'rgba(251,191,36,0.18)'; ctx.lineWidth = 1;
      roundRect(ctx, 14, noticeY, PX - 28, 44, 6); ctx.stroke();
      ctx.fillStyle = '#fbbf24'; ctx.font = 'bold 10px sans-serif';
      ctx.fillText('📷  No website screenshot — add SCREENSHOTONE_KEY for real screenshots', 24, noticeY + 16);
      ctx.fillStyle = '#6b7fa3'; ctx.font = '10px sans-serif';
      ctx.fillText('Free tier available at screenshotone.com · No credit card required', 24, noticeY + 32);
    };

    // Left panel: real pixels or clean execution view
    if (pagePixels && pagePixels.startsWith('data:')) {
      const img = new Image();
      img.onload = () => { ctx.drawImage(img, 0, 54, 880, H-54-36); draw(); };
      img.onerror = () => { drawExecutionView(); draw(); };
      img.src = pagePixels;
    } else if (pagePixels && pagePixels.startsWith('b64:')) {
      const img = new Image();
      img.onload = () => { ctx.drawImage(img, 0, 54, 880, H-54-36); draw(); };
      img.onerror = () => { drawExecutionView(); draw(); };
      img.src = 'data:image/png;base64,' + pagePixels.slice(4);
    } else {
      // No real pixels — clean execution view
      drawExecutionView();
      draw();
    }
  });
}


// ── Annotated screenshot showing test info + website URL ──────
function roundRect(ctx, x, y, w, h, r = 6) {
  // Safe polyfill — works in all browsers including older ones
  r = Math.min(r, w/2, h/2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

function generateAnnotatedScreenshot(test, url = '') {
  const canvas = document.createElement('canvas');
  canvas.width  = 1280;
  canvas.height = 800;
  const ctx = canvas.getContext('2d');

  // Background — simulates browser window
  ctx.fillStyle = '#0f1117';
  ctx.fillRect(0, 0, 1280, 800);

  // Browser chrome bar
  ctx.fillStyle = '#1e2433';
  ctx.fillRect(0, 0, 1280, 52);

  // Traffic lights
  [['#ff5f57',28], ['#febc2e',52], ['#28c840',76]].forEach(([c,x]) => {
    ctx.fillStyle = c; ctx.beginPath(); ctx.arc(x, 26, 7, 0, Math.PI*2); ctx.fill();
  });

  // URL bar
  ctx.fillStyle = '#2a3347';
  roundRect(ctx, 105, 12, 900, 28, 6);
  ctx.fill();
  ctx.fillStyle = test.status === 'passed' ? '#00d4aa' : '#f87171';
  ctx.font = '12px monospace';
  ctx.fillText('🔒 ' + (url || test.url || 'https://app-under-test.com'), 116, 30);

  // Status badge
  ctx.fillStyle = test.status === 'passed' ? 'rgba(0,212,170,0.15)' : 'rgba(248,113,113,0.15)';
  roundRect(ctx, 1040, 12, 220, 28, 6); ctx.fill();
  ctx.fillStyle = test.status === 'passed' ? '#00d4aa' : '#f87171';
  ctx.font = 'bold 12px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(test.status === 'passed' ? '✓ TEST PASSED' : '✗ TEST FAILED', 1150, 30);
  ctx.textAlign = 'left';

  // Page content area — draw simulated page
  ctx.fillStyle = '#0d1530';
  ctx.fillRect(0, 52, 1280, 748);

  // Simulate page elements based on test type
  drawSimulatedPage(ctx, test, url);

  // Test info overlay (bottom)
  ctx.fillStyle = 'rgba(10,15,30,0.92)';
  ctx.fillRect(0, 680, 1280, 120);

  // Separator line
  ctx.strokeStyle = test.status === 'passed' ? '#00d4aa' : '#f87171';
  ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(0, 680); ctx.lineTo(1280, 680); ctx.stroke();

  // Test details
  ctx.fillStyle = '#6b7fa3';
  ctx.font = '11px monospace';
  ctx.fillText(`ID: ${test.id || 'TC-001'}  |  TYPE: ${(test.type||'').toUpperCase()}  |  PRIORITY: ${test.priority||'High'}  |  ${new Date().toLocaleString()}`, 20, 700);

  ctx.fillStyle = '#f0f4ff';
  ctx.font = 'bold 15px sans-serif';
  const titleText = (test.name || '').substring(0, 90);
  ctx.fillText(titleText, 20, 724);

  if (test.testSteps) {
    ctx.fillStyle = '#94a3b8';
    ctx.font = '11px sans-serif';
    const steps = test.testSteps.split(' | ').slice(0, 3);
    steps.forEach((s, i) => ctx.fillText(`${i+1}. ${s.substring(0, 120)}`, 20, 744 + i*14));
  }

  if (test.expectedResult) {
    ctx.fillStyle = '#6b7fa3';
    ctx.font = '10px sans-serif';
    ctx.fillText('Expected: ' + test.expectedResult.substring(0, 140), 20, 792);
  }

  return canvas.toDataURL('image/png');
}

function drawSimulatedPage(ctx, test, url) {
  const type = test.type || 'ui';

  // Nav bar
  ctx.fillStyle = '#111b3a';
  ctx.fillRect(0, 52, 1280, 56);
  ctx.fillStyle = '#00d4aa';
  ctx.font = 'bold 18px sans-serif';
  ctx.fillText('⚡ YourApp', 24, 86);

  // Nav links
  ctx.fillStyle = '#94a3b8';
  ctx.font = '14px sans-serif';
  ['Home','Products','Pricing','About','Contact'].forEach((l,i) => {
    ctx.fillText(l, 160 + i*90, 86);
  });

  // CTA button
  ctx.fillStyle = '#00d4aa';
  roundRect(ctx, 1140, 66, 110, 28, 6); ctx.fill();
  ctx.fillStyle = '#0a0f1e';
  ctx.font = 'bold 12px sans-serif';
  ctx.fillText('Get Started', 1155, 84);

  if (type === 'ui' || type === 'security') {
    // Form-style page
    ctx.fillStyle = '#111b3a';
    roundRect(ctx, 390, 150, 500, 440, 12); ctx.fill();
    ctx.strokeStyle = 'rgba(0,212,170,0.2)';
    ctx.lineWidth = 1; ctx.stroke();

    ctx.fillStyle = '#f0f4ff';
    ctx.font = 'bold 22px sans-serif'; ctx.textAlign = 'center';
    ctx.fillText('Sign In', 640, 200);
    ctx.textAlign = 'left';

    // Form fields
    [['Email address', 180, '#1a2540'], ['Password', 260, '#1a2540']].forEach(([ph, y, bg]) => {
      ctx.fillStyle = bg; roundRect(ctx, 430, y + 80, 420, 44, 8); ctx.fill();
      ctx.strokeStyle = 'rgba(0,212,170,0.25)'; ctx.stroke();
      ctx.fillStyle = '#6b7fa3'; ctx.font = '13px sans-serif';
      ctx.fillText(ph, 448, y + 107);
    });

    // Submit button
    ctx.fillStyle = test.status === 'passed' ? '#00d4aa' : '#f87171';
    roundRect(ctx, 430, 480, 420, 46, 8); ctx.fill();
    ctx.fillStyle = '#0a0f1e'; ctx.font = 'bold 15px sans-serif'; ctx.textAlign = 'center';
    ctx.fillText(test.status === 'passed' ? '✓ Login Successful' : '✗ Login Failed', 640, 509);
    ctx.textAlign = 'left';

    // Test highlight overlay
    if (test.status === 'failed') {
      ctx.strokeStyle = '#f87171'; ctx.lineWidth = 2;
      roundRect(ctx, 428, 338, 424, 48, 8); ctx.stroke();
      ctx.fillStyle = 'rgba(248,113,113,0.1)'; ctx.fill();
      ctx.fillStyle = '#f87171'; ctx.font = '11px sans-serif';
      ctx.fillText('⚠ Assertion failed here', 432, 402);
    }

  } else if (type === 'api') {
    // API response view
    ctx.fillStyle = '#0a0f1e';
    roundRect(ctx, 80, 130, 580, 480, 8); ctx.fill();
    ctx.strokeStyle = 'rgba(0,212,170,0.2)'; ctx.stroke();

    ctx.fillStyle = '#111b3a';
    ctx.fillRect(80, 130, 580, 36);
    ctx.fillStyle = test.method === 'POST' ? '#f59e0b' : '#00d4aa';
    ctx.font = 'bold 13px monospace'; ctx.fillText(test.method || 'GET', 98, 153);
    ctx.fillStyle = '#94a3b8'; ctx.font = '13px monospace';
    ctx.fillText(test.endpoint || '/api/endpoint', 145, 153);

    const statusCode = test.status === 'passed' ? '200 OK' : '400 Bad Request';
    ctx.fillStyle = test.status === 'passed' ? '#4ade80' : '#f87171';
    ctx.fillText('← ' + statusCode, 98, 185);

    // JSON response
    ctx.fillStyle = '#4ade80'; ctx.font = '12px monospace';
    const lines = ['  "status": "ok",', '  "data": {', '    "token": "eyJhbGci..."', '  },', '  "timestamp": "' + new Date().toISOString() + '"'];
    ctx.fillText('{', 98, 210);
    lines.forEach((l,i) => {
      ctx.fillStyle = i%2===0 ? '#94a3b8' : '#00d4aa';
      ctx.fillText(l, 98, 230 + i*20);
    });
    ctx.fillStyle = '#94a3b8'; ctx.fillText('}', 98, 340);

    // Right: response details
    ctx.fillStyle = '#111b3a';
    roundRect(ctx, 700, 130, 500, 240, 8); ctx.fill();
    ctx.strokeStyle = 'rgba(0,212,170,0.2)'; ctx.stroke();
    ctx.fillStyle = '#6b7fa3'; ctx.font = '11px sans-serif';
    [['Status',test.status==='passed'?'200 OK':'400 Bad Request'],['Time',`${test.duration||0}ms`],['Size','1.2 KB'],['Type','application/json']].forEach(([k,v],i) => {
      ctx.fillStyle = '#6b7fa3'; ctx.fillText(k, 720, 162 + i*28);
      ctx.fillStyle = test.status==='passed'?'#4ade80':'#f87171'; ctx.fillText(v, 840, 162 + i*28);
    });

  } else if (type === 'performance') {
    // Performance metrics view
    const metrics = [['LCP','2.1s','#4ade80',85],['FID','45ms','#4ade80',90],['CLS','0.05','#4ade80',95],['TTFB','380ms','#f59e0b',72]];
    ctx.font = 'bold 20px sans-serif'; ctx.fillStyle = '#f0f4ff'; ctx.textAlign = 'center';
    ctx.fillText('Performance Report', 640, 140); ctx.textAlign = 'left';
    metrics.forEach(([name, val, color, score], i) => {
      const x = 100 + i*280, y = 200;
      ctx.fillStyle = '#111b3a'; roundRect(ctx, x, y, 240, 160, 10); ctx.fill();
      ctx.strokeStyle = color + '44'; ctx.stroke();
      ctx.fillStyle = color; ctx.font = 'bold 36px monospace'; ctx.textAlign = 'center';
      ctx.fillText(val, x+120, y+70);
      ctx.fillStyle = '#6b7fa3'; ctx.font = '13px sans-serif';
      ctx.fillText(name, x+120, y+100);
      // Score arc
      ctx.strokeStyle = color; ctx.lineWidth = 6;
      ctx.beginPath(); ctx.arc(x+120, y+130, 20, -Math.PI/2, (-Math.PI/2) + (score/100)*Math.PI*2);
      ctx.stroke();
      ctx.fillStyle = color; ctx.font = 'bold 11px sans-serif';
      ctx.fillText(score, x+112, y+135);
      ctx.textAlign = 'left';
    });
  } else {
    // Generic test page
    ctx.fillStyle = '#111b3a';
    roundRect(ctx, 80, 140, 800, 60, 8); ctx.fill();
    ctx.fillStyle = test.status==='passed' ? '#00d4aa' : '#f87171';
    ctx.font = 'bold 16px sans-serif';
    ctx.fillText(test.status==='passed' ? '✓ Test assertion passed' : '✗ Test assertion failed', 100, 177);

    const steps = (test.testSteps||'').split(' | ').slice(0,6);
    steps.forEach((s,i) => {
      ctx.fillStyle = i < steps.length-1 ? '#4ade80' : (test.status==='passed'?'#4ade80':'#f87171');
      ctx.font = '13px sans-serif';
      ctx.fillText(`${i===steps.length-1?'►':'✓'} ${s.substring(0,80)}`, 100, 240+i*36);
    });
  }
}

// ── ONE iframe attempt per URL ────────────────────────────────
// Returns dataUrl string on success, null on any failure.
// Never throws. Never leaves iframes in the DOM.
// Only ONE attempt per unique URL — result cached for all tests.
const _iframeCache    = new Map();  // url → null | dataUrl
const _iframeInFlight = new Map();  // url → Promise

function tryIframeOnce(url) {
  if (_iframeCache.has(url))    return Promise.resolve(_iframeCache.get(url));
  if (_iframeInFlight.has(url)) return _iframeInFlight.get(url);

  const promise = new Promise(resolve => {
    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      document.removeEventListener('securitypolicyviolation', cspListener);
      try { if (iframe.parentNode) document.body.removeChild(iframe); } catch {}
      _iframeCache.set(url, result);
      _iframeInFlight.delete(url);
      resolve(result);
    };

    const timer = setTimeout(() => finish(null), 3000);

    const cspListener = (e) => {
      if (e.blockedURI && (
        e.violatedDirective?.includes('frame') ||
        e.effectiveDirective?.includes('frame')
      )) finish(null);
    };
    document.addEventListener('securitypolicyviolation', cspListener);

    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:fixed;left:-9999px;top:-9999px;width:1280px;height:800px;border:none;opacity:0;pointer-events:none;z-index:-9999;visibility:hidden;';

    iframe.onload = () => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc || !doc.body) { finish(null); return; }
        const html = doc.documentElement?.outerHTML || '';
        if (html.length < 200) { finish(null); return; }

        const W = 1280, H = 800;
        const canvas = document.createElement('canvas');
        canvas.width = W; canvas.height = H;
        const ctx = canvas.getContext('2d');
        const svg = '<svg xmlns="http://www.w3.org/2000/svg" width="' + W + '" height="' + H + '"><foreignObject width="100%" height="100%"><div xmlns="http://www.w3.org/1999/xhtml" style="width:' + W + 'px;height:' + H + 'px;overflow:hidden">' + html.substring(0, 80000) + '</div></foreignObject></svg>';
        const img = new Image();
        img.onload = () => { try { ctx.drawImage(img, 0, 0); finish(canvas.toDataURL('image/png')); } catch { finish(null); } };
        img.onerror = () => finish(null);
        img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
      } catch { finish(null); }
    };

    iframe.onerror = () => finish(null);
    document.body.appendChild(iframe);
    iframe.src = url;
  });

  _iframeInFlight.set(url, promise);
  return promise;
}


